/** The built ground within 300 m (all 360°), the terrace floor and its stone walls — from site.ts. */
import * as THREE from 'three';
import { EYE, PLATFORM_OUTLINE } from '../composition';
import { LANE, TERRACE_BAND_M, UNDER_FLOOR_Y, lanePoint, naturalY, surfaceY, terraceEdges } from '../site';

export const TERRAIN_RADIUS_M = 300;
const AZ_SEGMENTS = 480;

/** Ring radii: 0.5 m steps near the terrace, coarser outward. */
export function terrainRings(): number[] {
  const rings: number[] = [];
  let r = 0;
  while (r < TERRAIN_RADIUS_M) {
    rings.push(r);
    r += r < 30 ? 0.5 : r < 60 ? 1 : r < 120 ? 3 : 10;
  }
  rings.push(TERRAIN_RADIUS_M);
  return rings;
}

export function buildTerrain(): THREE.Mesh {
  const rings = terrainRings();
  const pos = new Float32Array(rings.length * (AZ_SEGMENTS + 1) * 3);
  const idx: number[] = [];
  let k = 0;
  rings.forEach((r, i) => {
    for (let a = 0; a <= AZ_SEGMENTS; a++) {
      const t = (a / AZ_SEGMENTS) * Math.PI * 2;
      const x = EYE[0] + r * Math.sin(t);
      const z = EYE[2] - r * Math.cos(t);
      pos[k++] = x;
      pos[k++] = surfaceY(x, z);
      pos[k++] = z;
      if (i > 0 && a > 0) {
        const cur = i * (AZ_SEGMENTS + 1) + a;
        const prev = cur - (AZ_SEGMENTS + 1);
        idx.push(prev - 1, prev, cur - 1, prev, cur, cur - 1);
      }
    }
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  const mesh = new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color: 0x3d4a32, roughness: 1 }));
  mesh.name = 'ground_terrain';
  return mesh;
}

/** The terrace floor at y = 0. */
export function buildTerraceFloor(): THREE.Mesh {
  const shape = new THREE.Shape(PLATFORM_OUTLINE.map(([x, z]) => new THREE.Vector2(x, -z)));
  const g = new THREE.ShapeGeometry(shape);
  g.rotateX(-Math.PI / 2); // shape (x, -z) in XY -> world (x, 0, z), facing up
  const mesh = new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color: 0x77766f, roughness: 0.92 }));
  mesh.name = 'ground_platform';
  mesh.userData = { snow: true, wet: true };
  return mesh;
}

const WALL_STEP_M = 0.5;
/** Retaining faces reach this far below the ground outside: the grid triangles that cross the wall line dip under. */
const BURY_M = 3;
/** The ground strip along each wall: width, rows, and lift above the natural ground (covers the grid's facets). */
const STRIP_M = TERRACE_BAND_M;
const STRIP_ROWS = 6;
const STRIP_LIFT_M = 0.06;

/**
 * Stone walls along every terrace edge, decided every 0.5 m by the natural ground just outside (an edge can pass from
 * fill to cut): where it falls away, a retaining face from the floor down into the ground; where it rises above the
 * floor, a face up to it. Along the foot/top of each, a strip of ground that follows the slope exactly: the terrain
 * grid is not aligned with the walls, and its triangles crossing a wall line leave a sawtooth of peaks and notches.
 */
export function buildTerraceWalls(): THREE.Group {
  const pos: number[] = [];
  const top: number[] = [];
  const push = (out: number[], a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3): void => {
    out.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z, a.x, a.y, a.z, c.x, c.y, c.z, d.x, d.y, d.z);
  };
  const quad = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3): void => push(pos, a, b, c, d);
  const earth = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3): void => push(top, a, b, c, d);
  for (const e of terraceEdges()) {
    const len = Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]);
    const n = Math.max(1, Math.ceil(len / WALL_STEP_M));
    const at = (t: number, out: number, y: number): THREE.Vector3 =>
      new THREE.Vector3(e.a[0] + (e.b[0] - e.a[0]) * t + e.outward[0] * out, y, e.a[1] + (e.b[1] - e.a[1]) * t + e.outward[1] * out);
    const ground = (t: number, out: number): number => {
      const p = at(t, Math.max(out, 0.02), 0);
      return naturalY(p.x, p.z) + STRIP_LIFT_M;
    };
    for (let i = 0; i < n; i++) {
      const t0 = i / n;
      const t1 = (i + 1) / n;
      // the face: from the floor down to below the strip, or from under the floor up to the strip
      const faceTop = (t: number): number => Math.max(0.02, ground(t, 0));
      const faceBottom = (t: number): number => Math.min(ground(t, 0) - BURY_M, UNDER_FLOOR_Y);
      quad(at(t0, 0, faceTop(t0)), at(t1, 0, faceTop(t1)), at(t1, 0, faceBottom(t1)), at(t0, 0, faceBottom(t0)));
      for (let k = 0; k < STRIP_ROWS; k++) {
        const o0 = (k * STRIP_M) / STRIP_ROWS;
        const o1 = ((k + 1) * STRIP_M) / STRIP_ROWS;
        earth(at(t0, o0, ground(t0, o0)), at(t1, o0, ground(t1, o0)), at(t1, o1, ground(t1, o1)), at(t0, o1, ground(t0, o1)));
      }
    }
  }
  const group = new THREE.Group();
  group.name = 'ground_walls';
  for (const [name, data, color] of [['ground_walls_stone', pos, 0x8a877f], ['ground_walls_top', top, 0x3d4a32]] as const) {
    if (data.length === 0) continue;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(data, 3));
    g.computeVertexNormals();
    const mesh = new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color, roughness: 0.95, side: THREE.DoubleSide }));
    mesh.name = name;
    group.add(mesh);
  }
  return group;
}

/** The lane's surface: a ribbon along its arc, just above the levelled ground. */
export function buildLane(): THREE.Mesh {
  const pos: number[] = [];
  const idx: number[] = [];
  const steps = Math.ceil((LANE.toAzDeg - LANE.fromAzDeg) / 0.5);
  for (let i = 0; i <= steps; i++) {
    const az = LANE.fromAzDeg + ((LANE.toAzDeg - LANE.fromAzDeg) * i) / steps;
    for (const off of [-LANE.widthM / 2, LANE.widthM / 2]) {
      const [x, z] = lanePoint(az, off);
      pos.push(x, surfaceY(x, z) + 0.03, z);
    }
    if (i > 0) {
      const a = (i - 1) * 2;
      idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  const mesh = new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color: 0x5d5a55, roughness: 0.9, side: THREE.DoubleSide }));
  mesh.name = 'ground_lane';
  mesh.userData = { snow: true, wet: true };
  return mesh;
}
